import axios from 'axios';

// Base API URL
const BASE_URL = 'https://staff-tracking-api.routeeye.io/';
const DA_URL = 'https://da.routeeye.io';

// Create axios instance (existing APIs)
export const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests automatically
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('retiss_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-refresh token on 401
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

// The backend currently returns 500 (instead of 401) for an expired JWT.
const isExpiredJwtError = (error: any): boolean => {
  const status = error.response?.status;
  if (status === 401) return true;
  if (status === 500) {
    const message = JSON.stringify(error.response?.data ?? '').toLowerCase();
    return message.includes('jwt') && message.includes('expired');
  }
  return false;
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (isExpiredJwtError(error) && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('retiss_refresh_token');
      if (!refreshToken) {
        localStorage.clear();
        window.location.href = '/';
        return Promise.reject(error);
      }
      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshSubscribers.push((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(apiClient(originalRequest));
          });
        });
      }
      isRefreshing = true;
      try {
        const res = await axios.post(`${BASE_URL}auth/refresh`, { refreshToken });
        const newToken = res.data.accessToken;
        localStorage.setItem('retiss_token', newToken);
        onRefreshed(newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch {
        localStorage.clear();
        window.location.href = '/';
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// DA API client (auth/user management APIs at da.routeeye.io)
export const daApiClient = axios.create({
  baseURL: DA_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

daApiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('retiss_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

daApiClient.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);